import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  startOfMonth,
  endOfMonth,
  addDays,
  format,
  isSameDay,
  addMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  differenceInCalendarDays,
  sub,
} from "date-fns";
import { FaChevronLeft, FaChevronRight, FaHome, FaUsers } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import axios from "axios";
import API_BASE_URL from "../config";

const TimesheetView = ({ embedded = false, sidebarCollapsed = false, projectId = null, projectName = "" }) => {
  const navigate = useNavigate();
  const invalidDateToastShown = useRef(false);
  const groupByRef = useRef(null);
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [worklogs, setWorklogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [viewMode, setViewMode] = useState("monthly");
  const [billableFilter, setBillableFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("issue"); // "issue" | "employee"
  const [allEmpData, setAllEmpData] = useState({}); // { [empId]: { worklogs, tasks } }
  const [allEmpLoading, setAllEmpLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // expanded group keys
  const [pageLoading, setPageLoading] = useState(true);   // initial page load
  const [dataLoading, setDataLoading] = useState(false); // employee switch

  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [error, setError] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [holidayMap, setHolidayMap] = useState({});
  const [weeklyHolidays, setWeeklyHolidays] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [financialYear, setFinancialYear] = useState("");

  const token = localStorage.getItem("token");
  const dropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowEmployeeDropdown(false);
      }
      if (groupByRef.current && !groupByRef.current.contains(e.target)) {
        setShowGroupByMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  const formatHours = (n) =>
    Number.isFinite(n) ? round2(n).toFixed(2) : "0.00";

  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    //  check admin role
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(res => res.json())
      .then(data => {


        // API returns array → take first record
        const company = Array.isArray(data) ? data[0] : data;

        if (!company?.financialYear) {
          console.warn("No financialYear found");
          return;
        }

        setFinancialYear(company.financialYear);
      })

      .catch(err => console.error("Error fetching company info:", err));
  }, []);

  // Load employees — scoped to project if projectId is provided
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/", { replace: true });
      return;
    }

    const fetchEmployees = async () => {
      try {
        let empList = [];

        if (projectId && projectId !== "all") {
          // Fetch only employees assigned to this project
          const res = await axios.get(`${API_BASE_URL}/api/manageteam/project/${projectId}/employees`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          empList = Array.isArray(res.data) ? res.data : [];
        } else {
          // Fetch all active employees
          const res = await axios.get(`${API_BASE_URL}/api/employees/active`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          empList = Array.isArray(res.data) ? res.data : [];
        }

        setEmployees(empList);

        // Auto-select: prefer logged-in employee, else first in list
        const loggedEmployeeId = localStorage.getItem("employeeId");
        if (loggedEmployeeId) {
          const loggedEmployee = empList.find(
            (emp) => String(emp.id) === String(loggedEmployeeId)
          );
          setSelectedEmployee(loggedEmployee || empList[0] || null);
        } else {
          setSelectedEmployee(empList[0] || null);
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Unable to fetch employees.");
      } finally {
        setPageLoading(false);
      }
    };

    fetchEmployees();
  }, [navigate, projectId]);

  // Reset loading state when project changes
  useEffect(() => {
    setPageLoading(true);
    setSelectedEmployee(null);
    setEmployees([]);
  }, [projectId]);
  useEffect(() => {
    // Bootstrap CSS
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    bootstrapLink.id = "timesheetgrid-bootstrap";
    document.head.appendChild(bootstrapLink);

    // Bootstrap Icons CSS
    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    iconsLink.id = "timesheetgrid-icons";
    document.head.appendChild(iconsLink);

    return () => {
      const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
      if (existingBootstrap) existingBootstrap.remove();

      const existingIcons = document.getElementById("timesheetgrid-icons");
      if (existingIcons) existingIcons.remove();
    };
  }, []);


  useEffect(() => {
    fetchWeeklyHolidays();
  }, []);

  async function fetchWeeklyHolidays() {
    try {
      const res = await axios.get("/api/holidays/weekly", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const days = res.data.map((h) => h.name);
      setWeeklyHolidays(days);
    } catch (err) {
      console.error("Failed to fetch weekly holidays:", err);
    }
  }


  const isWeeklyHoliday = (day) => {
    const dayName = format(day, "EEEE"); // e.g. "Sunday"
    return weeklyHolidays.includes(dayName);
  };


  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/holidays`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch holidays");
        }

        const data = await res.json();

        const allHolidayDates = [];
        const holidayNameMap = {};

        data.forEach((h) => {
          const start = new Date(h.startDate);
          const end = new Date(h.endDate);

          // Go through each date between start and end
          for (
            let d = new Date(start);
            d <= end;
            d.setDate(d.getDate() + 1)
          ) {
            const formatted = d.toISOString().split("T")[0];
            allHolidayDates.push(formatted);
            holidayNameMap[formatted] = h.name;
          }
        });

        setHolidayDates(allHolidayDates);
        setHolidayMap(holidayNameMap);
        console.log("Fetched holidays:", allHolidayDates);
      } catch (err) {
        console.error("Error fetching holidays:", err);
      }
    };

    fetchHolidays();
  }, [token]);


  // Load worklogs and tasks when employee changes
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!selectedEmployee || !token) return;

    const fetchWorklogsAndTasks = async () => {
      setDataLoading(true);

      try {
        //  Fetch worklogs and tasks in parallel
        const tasksUrl = (projectId && projectId !== "all")
          ? `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks/project/${projectId}`
          : `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`;

        const [logsRes, tasksRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(tasksUrl, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const allWorklogs = Array.isArray(logsRes.data) ? logsRes.data : [];
        const allTasks    = Array.isArray(tasksRes.data) ? tasksRes.data : [];

        //  Build set of task IDs belonging to selected project (already project-filtered from API)
        const projectTaskIds = new Set(allTasks.map(t => t.id));

        //  Filter worklogs by date range AND by project task IDs (when project is selected)
        const filteredLogs = allWorklogs.filter((log) => {
          if (!log.date) return false;
          const logDate = new Date(log.date);
          const inRange = logDate >= startDate && logDate <= endDate;
          const inProject = (projectId && projectId !== "all")
            ? projectTaskIds.has(log.taskId)
            : true;
          return inRange && inProject;
        });

        setWorklogs(filteredLogs);

        //  Extract task IDs that have worklogs in this period
        const taskIdSet = new Set(filteredLogs.map((log) => log.taskId).filter(Boolean));

        //  Keep only tasks with worklogs in this period
        const filteredTasks = allTasks.filter((task) => taskIdSet.has(task.id));

        setTasks(filteredTasks);
      } catch (err) {
        console.error("Error fetching worklogs:", err);
        setError("Unable to fetch timesheet data.");
      } finally {
        setDataLoading(false);
      }
    };

    fetchWorklogsAndTasks();
  }, [selectedEmployee, startDate, endDate, projectId]);






  // Fetch ALL employees' worklogs+tasks when groupBy === "employee"
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (groupBy !== "employee" || !employees.length || !token) return;

    const fetchAllEmpData = async () => {
      setAllEmpLoading(true);
      try {
        const results = await Promise.all(
          employees.map(async (emp) => {
            try {
              const tasksUrl = (projectId && projectId !== "all")
                ? `${API_BASE_URL}/api/worklogs/employee/${emp.id}/tasks/project/${projectId}`
                : `${API_BASE_URL}/api/worklogs/employee/${emp.id}/tasks`;

              const [logsRes, tasksRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/worklogs/employee/${emp.id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(tasksUrl, {
                  headers: { Authorization: `Bearer ${token}` },
                }),
              ]);
              const allLogs = Array.isArray(logsRes.data) ? logsRes.data : [];
              const allTasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];

              // Filter logs by date range only
              // (employees are already scoped to the selected project)
              const filteredLogs = allLogs.filter((log) => {
                if (!log.date) return false;
                const d = new Date(log.date);
                return d >= startDate && d <= endDate;
              });

              const taskIdSet = new Set(filteredLogs.map((l) => l.taskId).filter(Boolean));
              const filteredTasks = allTasks.filter((t) => taskIdSet.has(t.id));
              return { empId: emp.id, worklogs: filteredLogs, tasks: filteredTasks };
            } catch {
              return { empId: emp.id, worklogs: [], tasks: [] };
            }
          })
        );
        const map = {};
        results.forEach(({ empId, worklogs, tasks }) => {
          map[empId] = { worklogs, tasks };
        });
        setAllEmpData(map);
        // All groups collapsed by default
        setExpandedGroups(new Set());
      } catch (err) {
        console.error("Error fetching all employee data:", err);
      } finally {
        setAllEmpLoading(false);
      }
    };

    fetchAllEmpData();
  }, [groupBy, employees, startDate, endDate, projectId]);

  const fetchLeaves = async () => {
    if (!selectedEmployee || !selectedEmployee.id || !financialYear) {
      console.warn("Employee or financial year missing — skipping fetchLeaves");
      setLeaveDates([]);
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/leave-transactions/employee/${selectedEmployee.id}/avail`,
        {
          params: { financialYear },   //  important
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const leaveList = [];

      //  Only APPROVED AV leaves
      res.data
        .filter(lv => lv.leaveStatus === "APPROVED") //  backend field
        .forEach(lv => {
          const start = new Date(lv.startDate);
          const end = new Date(lv.endDate);

          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            leaveList.push(d.toISOString().split("T")[0]);
          }
        });

      setLeaveDates(leaveList);
    } catch (err) {
      console.error("Error fetching leave:", err);
      setLeaveDates([]);
    }
  };


  useEffect(() => {
    if (selectedEmployee && financialYear) {
      fetchLeaves();
    }
  }, [selectedEmployee, financialYear]);



  const isLeaveDay = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return leaveDates.includes(formatted);
  };



  const isHolidayColumn = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return holidayDates.includes(formatted);
  };


  const handleExport = async (fileFormat) => {
    if (!selectedEmployee) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      // Format dates as yyyy-MM-dd
      const fromDateStr = startDate ? startDate.toISOString().split("T")[0] : "";
      const toDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

      // Build URL params
      const params = new URLSearchParams();
      params.append("type", "date"); // must be "date" for fromDate/toDate filter
      if (fromDateStr) params.append("fromDate", fromDateStr);
      if (toDateStr) params.append("toDate", toDateStr);

      // Add billing type separately as a query param if needed
      if (billableFilter && billableFilter !== "all") params.append("billingType", billableFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/timesheet/${selectedEmployee.id}?${params.toString()}&format=${fileFormat}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          fileFormat === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Timesheet_${selectedEmployee.firstName}_${selectedEmployee.lastName}.${fileFormat === "pdf" ? "pdf" : "xlsx"
        }`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    }
  };






  // Generate dynamic days
  const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
  const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

  const handlePrev = () => {
    if (viewMode === "monthly") {
      const newStart = sub(startDate, { months: 1 });
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = sub(startDate, { weeks: 1 });
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  const handleNext = () => {
    if (viewMode === "monthly") {
      const newStart = addMonths(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = addWeeks(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };



  const filteredWorklogs = (() => {
    if (!startDate && !endDate) return worklogs;

    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;

    if (from && to && to < from) {
      if (!invalidDateToastShown.current) {
        toast.error("End date must be greater than or equal to Start date.");
        invalidDateToastShown.current = true;
      }
      return worklogs;
    } else {
      invalidDateToastShown.current = false;
    }

    return worklogs.filter((log) => {
      const logDate = new Date(log.date);

      const inStart = from ? logDate >= from : true;
      const inEnd = to ? logDate <= to : true;

      if (!(inStart && inEnd)) return false;

      if (billableFilter === "all") return true;

      return log.billingType === billableFilter;
    });
  })();




  const getHours = (taskId, day, billableType) =>
    round2(
      filteredWorklogs
        .filter(
          (log) =>
            log.taskId === taskId &&
            isSameDay(new Date(log.date), day) &&
            (!billableType || log.billingType === billableType)
        )
        .reduce((sum, log) => sum + log.hours, 0)
    );

  // For group-by-employee: get hours for a specific employee+task+day
  const getHoursForEmp = (empId, taskId, day, billableType) => {
    const logs = (allEmpData[empId]?.worklogs || []).filter(
      (log) =>
        log.taskId === taskId &&
        isSameDay(new Date(log.date), day) &&
        (!billableType || log.billingType === billableType) &&
        (billableFilter === "all" || log.billingType === billableFilter)
    );
    return round2(logs.reduce((s, l) => s + l.hours, 0));
  };

  // Total hours for an employee on a given day (all tasks)
  const getEmpDayTotal = (empId, day, billableType) => {
    const tasks = allEmpData[empId]?.tasks || [];
    return round2(tasks.reduce((s, t) => s + getHoursForEmp(empId, t.id, day, billableType), 0));
  };

  // Total hours for an employee across the period
  const getEmpPeriodTotal = (empId, billableType) => {
    const logs = (allEmpData[empId]?.worklogs || []).filter(
      (log) =>
        (!billableType || log.billingType === billableType) &&
        (billableFilter === "all" || log.billingType === billableFilter)
    );
    return round2(logs.reduce((s, l) => s + l.hours, 0));
  };

  // For group-by-issue: all employees' hours for a task on a day
  const getTaskDayTotalAllEmps = (taskId, day, billableType) => {
    return round2(
      employees.reduce((sum, emp) => sum + getHoursForEmp(emp.id, taskId, day, billableType), 0)
    );
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Stats: compute from the right data source based on groupBy mode ──────
  // Group by Issue  → use filteredWorklogs (selected employee only)
  // Group by Employee → sum across all employees in allEmpData

  const allEmpWorklogs = groupBy === "employee"
    ? Object.values(allEmpData).flatMap(d => d.worklogs || []).filter(log => {
        if (billableFilter === "all") return true;
        return log.billingType === billableFilter;
      })
    : null;

  const sourceWorklogs = groupBy === "employee" ? allEmpWorklogs : filteredWorklogs;

  const totalHours = round2(sourceWorklogs.reduce((sum, log) => sum + log.hours, 0));

  const totalBillable = round2(
    sourceWorklogs
      .filter(log => log.billingType === "billable")
      .reduce((sum, log) => sum + log.hours, 0)
  );

  const totalNonBillable = round2(
    sourceWorklogs
      .filter(log => log.billingType === "non-billable")
      .reduce((sum, log) => sum + log.hours, 0)
  );

  const totalFilteredHours       = totalHours;
  const totalFilteredBillable    = totalBillable;
  const totalFilteredNonBillable = totalNonBillable;

  const HOURS_PER_DAY = 8;

  // total days of month
  const monthDays = days.length;

  // working days
  const workingDays = days.filter((day) => {
    const formatted = format(day, "yyyy-MM-dd");

    return (
      !isWeeklyHoliday(day) &&
      !holidayDates.includes(formatted) &&
      !leaveDates.includes(formatted)
    );
  }).length;

  const totalMonthHours = workingDays * HOURS_PER_DAY;

  // filteredWorklogs is already scoped to project (done at fetch time)
  // so loggedMonthHours correctly reflects only this project's hours
  const projectTaskIdSet = new Set(tasks.map(t => t.id));
  const loggedMonthHours = totalFilteredHours;


  const getFooterBg = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    const isHoliday = holidayDates.includes(formatted);
    const isWeekly = isWeeklyHoliday(day);
    const isLeave = leaveDates.includes(formatted);

    if (isLeave) return "#fff0f0";       // Leave
    if (isHoliday) return "#d4f8d4";     // Holiday
    if (isWeekly) return "#f7f7f7";      // Weekly off
    return "#f8f9fa";                    // Default footer bg-light
  };



  const utilizationPct = totalMonthHours > 0
    ? Math.min(100, Math.round((loggedMonthHours / totalMonthHours) * 100))
    : 0;

  // per-day total hours logged (all tasks) — respects groupBy mode
  const getDayTotal = (day) => {
    if (groupBy === "employee") {
      return round2(
        employees.reduce((sum, emp) => sum + getEmpDayTotal(emp.id, day, undefined), 0)
      );
    }
    return round2(tasks.reduce((sum, t) => sum + getHours(t.id, day), 0));
  };

  // color for day header circle: green=met, orange=partial, red=missed, gray=off
  const getDayStatus = (day) => {
    if (isWeeklyHoliday(day) || isHolidayColumn(day)) return "off";
    if (isLeaveDay(day)) return "leave";
    const hrs = getDayTotal(day);
    if (hrs === 0) return "empty";
    if (hrs >= HOURS_PER_DAY) return "met";
    return "partial";
  };

  const DAY_STATUS_STYLES = {
    off:     { bg: "#F1F2F4", color: "#626F86", border: "#DFE1E6" },
    leave:   { bg: "#FFF0F0", color: "#AE2A19", border: "#FFD2CC" },
    empty:   { bg: "#FFFFFF", color: "#8590A2", border: "#DFE1E6" },
    partial: { bg: "#FFF7D6", color: "#974F0C", border: "#F8C73F" },
    met:     { bg: "#DCFFF1", color: "#1F845A", border: "#4BCE97" },
  };


  if (pageLoading) return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:300,gap:16,fontFamily:"system-ui,sans-serif" }}>
      <style>{`@keyframes _spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:36,height:36,border:"3px solid #E8EAED",borderTopColor:"#0052CC",borderRadius:"50%",animation:"_spin .8s linear infinite" }}/>
      <span style={{ fontSize:13,color:"#626F86",fontWeight:500 }}>Loading timesheet…</span>
    </div>
  );

  /* ─── colour tokens (Atlassian DS) ─────────────────────────────────────── */
  const C = {
    blue  :"#0052CC", blueLt:"#DEEBFF", blueMd:"#2684FF",
    green :"#1F845A", greenLt:"#E3FCEF", greenMd:"#4BCE97",
    red   :"#AE2A19", redLt  :"#FFEBE6", redMd  :"#FF8F73",
    amber :"#974F0C", amberLt:"#FFF7D6", amberMd:"#F8C73F",
    purple:"#5243AA", purpleLt:"#EAE6FF", purpleMd:"#998DD9",
    n900  :"#172B4D", n700:"#44546F", n500:"#626F86", n400:"#8590A2",
    n200  :"#B3BAC5", n100:"#DFE1E6", n40:"#F1F2F4", n20:"#F7F8FA", n10:"#FAFBFC",
    white :"#ffffff",
  };

  const DAY_ST = {
    met    :{ bg:C.greenLt, border:C.greenMd, color:C.green   },
    partial:{ bg:C.amberLt, border:C.amberMd, color:C.amber   },
    leave  :{ bg:C.redLt,   border:C.redMd,   color:C.red     },
    off    :{ bg:C.n40,     border:C.n100,    color:C.n500    },
    empty  :{ bg:C.white,   border:C.n100,    color:C.n400    },
  };

  const COL_W = { iss:260, day:50, tot:72 };

  return (
    <>
      <style>{`
        /* ─── Base ──────────────────────────────────────────────────────────── */
        .t*{box-sizing:border-box}
        .t{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif;font-size:13px;color:${C.n900};background:${C.n20};min-height:100%}
        .t.te{background:transparent}
        @keyframes _spin{to{transform:rotate(360deg)}}
        @keyframes _fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}

        /* ─── Toolbar card ──────────────────────────────────────────────────── */
        .tb{background:transparent;padding:0 0 10px 0;display:flex;flex-direction:row;align-items:center;flex-wrap:wrap;gap:8px;margin-bottom:12px;border-bottom:1px solid ${C.n100}}
        .tb-row{display:flex;align-items:center;flex-wrap:wrap;gap:8px}
        .tb-l{display:flex;align-items:center;gap:8px;flex-wrap:wrap;flex:1;min-width:0}
        .tb-r{display:flex;align-items:center;gap:6px;flex-shrink:0;flex-wrap:wrap}
        .tb-sep{width:1px;height:22px;background:${C.n100};flex-shrink:0}

        /* Employee picker */
        .ep{position:relative}
        .ep-btn{display:flex;align-items:center;gap:8px;padding:5px 10px 5px 5px;border:1.5px solid ${C.n100};border-radius:20px;background:${C.white};cursor:pointer;transition:border-color .15s,box-shadow .15s;white-space:nowrap;max-width:220px}
        .ep-btn:hover,.ep-btn.open{border-color:${C.blue};box-shadow:0 0 0 2px rgba(0,82,204,.1)}
        .av{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,${C.blue},${C.blueMd});color:${C.white};display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;flex-shrink:0;overflow:hidden}
        .av img{width:100%;height:100%;object-fit:cover}
        .ep-name{font-weight:600;font-size:13px;color:${C.n900};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0}
        .ep-menu{position:absolute;top:calc(100% + 6px);left:0;min-width:230px;background:${C.white};border:1px solid ${C.n100};border-radius:8px;box-shadow:0 8px 32px rgba(9,30,66,.2);z-index:400;max-height:270px;overflow-y:auto;animation:_fade .12s ease}
        .ep-item{display:flex;align-items:center;gap:10px;padding:8px 14px;cursor:pointer;transition:background .1s}
        .ep-item:hover{background:${C.n20}}
        .ep-item .av{width:26px;height:26px;font-size:10px}
        .ep-item-name{font-weight:600;font-size:12px;color:${C.n900}}
        .ep-item-role{font-size:11px;color:${C.n400};margin-top:1px}

        /* Period nav */
        .pn{display:flex;align-items:center;gap:4px}
        .pn-arrow{width:26px;height:26px;border:1.5px solid ${C.n100};border-radius:6px;background:${C.white};cursor:pointer;display:flex;align-items:center;justify-content:center;color:${C.n700};transition:all .15s;flex-shrink:0}
        .pn-arrow:hover{background:${C.n40};border-color:${C.n200}}
        .pn-label{font-weight:700;font-size:14px;color:${C.n900};min-width:120px;text-align:center;letter-spacing:-.02em}

        /* Segmented controls */
        .seg{display:flex;border:1.5px solid ${C.n100};border-radius:6px;overflow:hidden;background:${C.n20}}
        .seg-btn{padding:4px 12px;background:transparent;border:none;cursor:pointer;font-size:12px;font-weight:600;color:${C.n500};transition:all .15s;white-space:nowrap;line-height:18px}
        .seg-btn.on{background:${C.white};color:${C.blue};box-shadow:0 1px 4px rgba(9,30,66,.14)}

        /* Filter chips */
        .fc{display:flex;border:1.5px solid ${C.n100};border-radius:6px;overflow:hidden;background:${C.n20}}
        .fc-btn{padding:4px 11px;background:transparent;border:none;border-right:1px solid ${C.n100};cursor:pointer;font-size:12px;font-weight:600;color:${C.n500};transition:all .15s;white-space:nowrap;line-height:18px}
        .fc-btn:last-child{border-right:none}
        .fc-btn.fa{background:${C.greenLt};color:${C.green}}
        .fc-btn.fb{background:${C.blueLt};color:${C.blue}}
        .fc-btn.fn{background:${C.amberLt};color:${C.amber}}

        /* Date inputs */
        .di{border:1.5px solid ${C.n100};border-radius:6px;padding:4px 8px;font-size:12px;color:${C.n900};background:${C.white};outline:none;width:118px;transition:border-color .15s}
        .di:focus{border-color:${C.blue};box-shadow:0 0 0 2px rgba(0,82,204,.12)}

        /* Export btns */
        .xb{display:flex;align-items:center;gap:5px;padding:5px 12px;border:1.5px solid ${C.n100};border-radius:6px;background:${C.white};font-size:12px;font-weight:600;color:${C.n700};cursor:pointer;transition:all .15s;white-space:nowrap}
        .xb:hover{background:${C.n40};border-color:${C.n200}}

        /* ─── Summary strip ─────────────────────────────────────────────────── */
        .ss{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:12px}
        @media(max-width:900px){.ss{grid-template-columns:repeat(2,1fr)}}
        .sc{background:${C.white};border:1px solid ${C.n100};border-left:3px solid;border-radius:6px;padding:8px 12px;position:relative;overflow:hidden;box-shadow:0 1px 2px rgba(9,30,66,.05);animation:_fade .2s ease}
        .sc::before{display:none}
        .sc-top{display:flex;align-items:center;gap:5px;margin-bottom:4px}
        .sc-ico{width:20px;height:20px;border-radius:5px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .sc-lbl{font-size:10px;font-weight:600;color:${C.n500};text-transform:uppercase;letter-spacing:.05em}
        .sc-val{font-size:16px;font-weight:700;line-height:1;letter-spacing:-.02em}
        .sc-unit{font-size:11px;font-weight:500;color:${C.n400};margin-left:2px}
        .sc-bar{height:3px;background:${C.n40};border-radius:99px;overflow:hidden;margin-top:5px}
        .sc-fill{height:100%;border-radius:99px;transition:width .6s cubic-bezier(.4,0,.2,1)}
        .sc-meta{display:flex;align-items:center;justify-content:space-between;margin-top:4px}
        .sc-sub{font-size:10px;color:${C.n400}}
        .sc-pct{font-size:11px;font-weight:700}

        /* ─── Grid card ─────────────────────────────────────────────────────── */
        .gc{background:${C.white};border:1px solid ${C.n100};border-radius:8px;overflow:clip;box-shadow:0 1px 2px rgba(9,30,66,.05);position:relative}
        .gs{overflow:auto;max-height:calc(100vh - 318px)}
        .ov{position:absolute;inset:0;background:rgba(255,255,255,.8);display:flex;align-items:center;justify-content:center;z-index:200;border-radius:8px;backdrop-filter:blur(2px)}
        .sp{width:24px;height:24px;border:3px solid ${C.n100};border-top-color:${C.blue};border-radius:50%;animation:_spin .7s linear infinite}

        /* ─── Table ─────────────────────────────────────────────────────────── */
        .tbl{width:100%;border-collapse:collapse;table-layout:fixed}
        .tbl th,.tbl td{border-bottom:1px solid ${C.n100};border-right:1px solid ${C.n100};padding:0}
        .tbl th:last-child,.tbl td:last-child{border-right:none}

        /* Issue col */
        .th-i{width:${COL_W.iss}px;min-width:${COL_W.iss}px;max-width:${COL_W.iss}px;position:sticky;left:0;z-index:30;background:${C.n10};padding:0 16px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:${C.n500};border-right:2px solid ${C.n100};border-bottom:2px solid ${C.n100};height:62px;vertical-align:middle;text-align:center}
        .td-i{width:${COL_W.iss}px;min-width:${COL_W.iss}px;max-width:${COL_W.iss}px;position:sticky;left:0;z-index:4;background:${C.white};border-right:2px solid ${C.n100}!important;transition:background .1s}

        /* Day cols */
        .th-d{width:${COL_W.day}px;min-width:${COL_W.day}px;max-width:${COL_W.day}px;text-align:center;padding:0;vertical-align:middle;background:${C.n10};border-bottom:2px solid ${C.n100};border-right:1px solid ${C.n100}}
        .td-d{width:${COL_W.day}px;min-width:${COL_W.day}px;max-width:${COL_W.day}px;text-align:center;vertical-align:middle;padding:0 2px;height:44px;border-right:1px solid ${C.n100}}

        /* Total col */
        .th-t{width:${COL_W.tot}px;min-width:${COL_W.tot}px;max-width:${COL_W.tot}px;position:sticky;right:0;z-index:30;background:${C.n10};text-align:center;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:${C.n500};border-left:2px solid ${C.n100};border-bottom:2px solid ${C.n100};border-right:none;height:62px;vertical-align:middle}
        .td-t{width:${COL_W.tot}px;min-width:${COL_W.tot}px;max-width:${COL_W.tot}px;position:sticky;right:0;z-index:4;background:${C.white};border-left:2px solid ${C.n100};border-right:none;text-align:center;font-weight:700;font-size:13px;padding:0 4px;transition:background .1s}

        /* Day header circle */
        .dn{font-size:9px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;line-height:1.2}

        /* Row */
        .tbl tbody tr{transition:background .1s}
        .tbl tbody tr:hover .td-i,.tbl tbody tr:hover .td-d,.tbl tbody tr:hover .td-t{background:${C.blueLt}!important}

        /* Issue cell */
        .ii{display:flex;align-items:center;padding:0 12px;gap:9px;height:44px}
        .idot{width:10px;height:10px;border-radius:2px;flex-shrink:0;margin-top:1px}
        .ib{flex:1;min-width:0}
        .ic{font-size:11px;font-weight:700;color:${C.blue};white-space:nowrap;line-height:1.2}
        .it{font-size:12px;color:${C.n900};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.2}
        .ip{font-size:10px;color:${C.n400};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.2;margin-top:1px}
        .ia{text-decoration:none;display:block}
        .ia:hover .ic{text-decoration:underline}

        /* Hour pill */
        .hp{display:inline-flex;align-items:center;justify-content:center;min-width:32px;height:20px;border-radius:10px;font-size:11px;font-weight:700;padding:0 6px;line-height:1}
        .em{color:${C.n200};font-size:15px;font-weight:300}

        /* Footer */
        .tf{position:sticky;bottom:0;z-index:15}
        .tfl{width:${COL_W.iss}px;min-width:${COL_W.iss}px;max-width:${COL_W.iss}px;position:sticky;left:0;z-index:6;background:${C.n20};border-right:2px solid ${C.n100};border-bottom:1px solid ${C.n100};padding:20px 16px 20px 24px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap}
        .tfd{width:${COL_W.day}px;min-width:${COL_W.day}px;max-width:${COL_W.day}px;text-align:center;font-weight:700;font-size:12px;padding:20px 2px;background:${C.n20};border-right:1px solid ${C.n100};border-bottom:1px solid ${C.n100}}
        .tft{width:${COL_W.tot}px;min-width:${COL_W.tot}px;max-width:${COL_W.tot}px;position:sticky;right:0;z-index:6;background:${C.n20};border-left:2px solid ${C.n100};border-right:none;border-bottom:1px solid ${C.n100};text-align:center;font-weight:700;font-size:13px;padding:20px 4px}
        .tf tr:first-child .tfl,.tf tr:first-child .tfd,.tf tr:first-child .tft{border-top:2px solid ${C.n100}}
        .tf tr+tr .tfl,.tf tr+tr .tfd,.tf tr+tr .tft{border-top:1px solid ${C.n100}}
        .tf tr:last-child .tfl,.tf tr:last-child .tfd,.tf tr:last-child .tft{border-bottom:none}

        /* Group header row */
        .grp-hdr td{background:${C.n20}!important;border-bottom:1px solid ${C.n100}}
        .grp-hdr .td-i{background:${C.n20}!important}
        .grp-hdr .td-t{background:${C.n20}!important}
        .grp-toggle{display:flex;align-items:center;gap:8px;padding:0 12px;height:40px;cursor:pointer;user-select:none}
        .grp-arrow{width:16px;height:16px;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:transform .15s;color:${C.n500}}
        .grp-arrow.open{transform:rotate(90deg)}
        .grp-av{width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,${C.blue},${C.blueMd});color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:10px;flex-shrink:0;overflow:hidden}
        .grp-av img{width:100%;height:100%;object-fit:cover}
        .grp-name{font-size:12px;font-weight:700;color:${C.n900}}
        .grp-count{font-size:10px;color:${C.n500};font-weight:500}
        .grp-total{font-size:12px;font-weight:700;color:${C.n700}}
        .grp-day{font-size:11px;font-weight:600;color:${C.n500}}

        /* Sub-rows indent */
        .sub-i .ii{padding-left:28px}
        .sub-emp .ii{padding-left:28px}

        /* Group By btn in toolbar */
        .gb-wrap{position:relative}
        .gb-btn{display:flex;align-items:center;gap:5px;padding:4px 10px;border:1.5px solid ${C.n100};border-radius:6px;background:${C.white};font-size:12px;font-weight:600;color:${C.n700};cursor:pointer;transition:all .15s;white-space:nowrap}
        .gb-btn:hover,.gb-btn.open{border-color:${C.blue};color:${C.blue};background:${C.blueLt}}
        .gb-menu{position:absolute;top:calc(100% + 4px);right:0;background:${C.white};border:1px solid ${C.n100};border-radius:8px;box-shadow:0 6px 20px rgba(9,30,66,.18);z-index:300;min-width:160px;overflow:hidden;animation:_fade .1s ease}
        .gb-item{display:flex;align-items:center;gap:8px;padding:9px 14px;cursor:pointer;font-size:12px;font-weight:500;color:${C.n900};transition:background .1s}
        .gb-item:hover{background:${C.n20}}
        .gb-item.active{background:${C.blueLt};color:${C.blue};font-weight:700}
        .gb-check{width:14px;height:14px;flex-shrink:0}
        .es{text-align:center;padding:72px 24px;color:${C.n400}}
        .es-icon{font-size:44px;margin-bottom:14px;opacity:.55}
        .es-title{font-size:15px;font-weight:600;color:${C.n700};margin-bottom:6px}
        .es-sub{font-size:13px}

        /* Scrollbar */
        .gs::-webkit-scrollbar{width:6px;height:6px}
        .gs::-webkit-scrollbar-track{background:transparent}
        .gs::-webkit-scrollbar-thumb{background:${C.n200};border-radius:99px}
        .gs::-webkit-scrollbar-thumb:hover{background:${C.n500}}
      `}</style>

      <ToastContainer position="top-right" autoClose={4000} />
      {!embedded && <div className="fixed-top"><AdminNavbar /></div>}

      <div className={`t${embedded ? " te" : ""}`} style={{
        padding: embedded ? "0 0 16px 0" : "76px 24px 24px",
        marginLeft: embedded ? "0px" : undefined,
        transition: embedded ? "margin-left 0.25s ease" : undefined,
      }}>

        {/* ═══════════════════ TOOLBAR ═══════════════════ */}
        <div className="tb">

            {/* Project badge */}
            {projectId && projectId !== "all" && projectName && (
              <div style={{ display:"flex", alignItems:"center", gap:5, padding:"3px 10px", background:C.blueLt, borderRadius:20, fontSize:12, fontWeight:700, color:C.blue, flexShrink:0 }}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="1" y="1" width="9" height="9" rx="1.5" stroke={C.blue} strokeWidth="1.5"/><path d="M3.5 5.5h4M5.5 3.5v4" stroke={C.blue} strokeWidth="1.4" strokeLinecap="round"/></svg>
                {projectName}
              </div>
            )}
            {projectId && projectId !== "all" && projectName && <div className="tb-sep" />}

            {/* Employee — only shown in issue mode */}
            {groupBy === "issue" && (
              <div className="ep" ref={dropdownRef}>
                <button className={`ep-btn${showEmployeeDropdown ? " open" : ""}`} onClick={() => setShowEmployeeDropdown(v => !v)}>
                  <div className="av">
                    {selectedEmployee?.photoPath
                      ? <img src={`${API_BASE_URL}/uploads/${selectedEmployee.photoPath}`} onError={e => { e.currentTarget.src = "/default-avatar.png"; }} alt="" />
                      : (selectedEmployee?.firstName?.charAt(0)?.toUpperCase() || "?")}
                  </div>
                  <span className="ep-name">
                    {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : "Select Employee"}
                  </span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink:0 }}>
                    <path d="M3 4.5l3 3 3-3" stroke="#626F86" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {showEmployeeDropdown && (
                  <div className="ep-menu">
                    {employees.map(emp => (
                      <div key={emp.id} className="ep-item" onClick={() => { setSelectedEmployee(emp); setShowEmployeeDropdown(false); }}>
                        <div className="av">
                          {emp.photoPath
                            ? <img src={`${API_BASE_URL}/uploads/${emp.photoPath}`} onError={e => { e.currentTarget.src = "/default-avatar.png"; }} alt="" />
                            : emp.firstName?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="ep-item-name">{emp.firstName} {emp.lastName}</div>
                          {emp.designation && <div className="ep-item-role">{emp.designation}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="tb-sep" />

            {/* Period nav */}
            <div className="pn">
              <button className="pn-arrow" onClick={handlePrev} title="Previous period">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M8.5 10L5 6.5 8.5 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className="pn-label">
                {viewMode === "monthly"
                  ? format(startDate, "MMMM yyyy")
                  : `${format(startDate, "d MMM")} – ${format(endDate, "d MMM yyyy")}`}
              </span>
              <button className="pn-arrow" onClick={handleNext} title="Next period">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M4.5 3L8 6.5 4.5 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Month / Week */}
            <div className="seg">
              <button className={`seg-btn${viewMode === "monthly" ? " on" : ""}`}
                onClick={() => { setViewMode("monthly"); setStartDate(startOfMonth(startDate)); setEndDate(endOfMonth(startDate)); }}>
                Month
              </button>
              <button className={`seg-btn${viewMode === "weekly" ? " on" : ""}`}
                onClick={() => { const t = new Date(); setViewMode("weekly"); setStartDate(startOfWeek(t, { weekStartsOn: 1 })); setEndDate(endOfWeek(t, { weekStartsOn: 1 })); }}>
                Week
              </button>
            </div>

            <div className="tb-sep" />

            {/* Date range */}
            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
              <input type="date" className="di"
                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                onChange={e => setStartDate(e.target.value ? new Date(e.target.value) : null)} />
              <svg width="18" height="10" viewBox="0 0 18 10" fill="none">
                <path d="M1 5h16M12 1l4 4-4 4" stroke="#B3BAC5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input type="date" className="di"
                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                onChange={e => {
                  const d = e.target.value ? new Date(e.target.value) : null;
                  if (d && startDate && d < startDate) { toast.error("End date cannot be before start date."); }
                  else setEndDate(d);
                }} />
            </div>

            <div className="tb-sep" />

            {/* Billable filter */}
            <div className="fc">
              <button className={`fc-btn${billableFilter === "all"         ? " fa" : ""}`} onClick={() => setBillableFilter("all")}>All</button>
              <button className={`fc-btn${billableFilter === "billable"     ? " fb" : ""}`} onClick={() => setBillableFilter("billable")}>Billable</button>
              <button className={`fc-btn${billableFilter === "non-billable" ? " fn" : ""}`} onClick={() => setBillableFilter("non-billable")}>Non-Billable</button>
            </div>

            <div className="tb-sep" />

            {/* Group By */}
            <div className="gb-wrap" ref={groupByRef}>
              <button className={`gb-btn${showGroupByMenu ? " open" : ""}`} onClick={() => setShowGroupByMenu(v => !v)}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                  <rect x="7" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                  <rect x="1" y="7" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                  <rect x="7" y="7" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
                Group: {groupBy === "issue" ? "Issue" : "Employee"}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {showGroupByMenu && (
                <div className="gb-menu">
                  {[
                    { key:"issue",    label:"Group by Issue",    icon:"🗂️" },
                    { key:"employee", label:"Group by Employee", icon:"👤" },
                  ].map(opt => (
                    <div key={opt.key} className={`gb-item${groupBy === opt.key ? " active" : ""}`}
                      onClick={() => { setGroupBy(opt.key); setShowGroupByMenu(false); setExpandedGroups(new Set()); }}>
                      <span style={{ fontSize:14 }}>{opt.icon}</span>
                      <span>{opt.label}</span>
                      {groupBy === opt.key && (
                        <svg className="gb-check" viewBox="0 0 14 14" fill="none" style={{ marginLeft:"auto" }}>
                          <path d="M2.5 7l3 3 6-6" stroke="#0052CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="tb-sep" />

            {/* Exports */}
            <button className="xb" onClick={() => handleExport("pdf")}>
              <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
                <path d="M6.5 1v9M3 7.5l3.5 3.5L10 7.5M1.5 13h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              PDF
            </button>
            <button className="xb" onClick={() => handleExport("excel")}>
              <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
                <path d="M6.5 1v9M3 7.5l3.5 3.5L10 7.5M1.5 13h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Excel
            </button>

          </div>{/* end tb */}

        {/* ═══════════════════ STATS STRIP ═══════════════════ */}
        <div className="ss">
          {[
            {
              label:"Total Logged", color:C.blue, borderColor:C.blue,
              iconBg:C.blueLt,
              icon:<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke={C.blue} strokeWidth="1.5"/><path d="M7.5 4.5V8l2.5 1.5" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
              val: formatHours(totalFilteredHours), unit:"h",
            },
            {
              label:"Billable", color:C.green, borderColor:C.green,
              iconBg:C.greenLt,
              icon:<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="3.5" width="12" height="9" rx="1.5" stroke={C.green} strokeWidth="1.5"/><path d="M7.5 6v3m-1.5-1.5h3" stroke={C.green} strokeWidth="1.5" strokeLinecap="round"/></svg>,
              val: formatHours(totalFilteredBillable), unit:"h",
            },
            {
              label:"Non-Billable", color:C.red, borderColor:C.red,
              iconBg:C.redLt,
              icon:<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke={C.red} strokeWidth="1.5"/><path d="M5 7.5h5" stroke={C.red} strokeWidth="1.8" strokeLinecap="round"/></svg>,
              val: formatHours(totalFilteredNonBillable), unit:"h",
            },
            {
              label:"Monthly Utilization", color:C.purple, borderColor:C.purple,
              iconBg:C.purpleLt,
              icon:<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="8" width="3" height="5.5" rx="1" fill={C.purple} opacity=".4"/><rect x="6" y="5" width="3" height="8.5" rx="1" fill={C.purple} opacity=".65"/><rect x="10.5" y="2" width="3" height="11.5" rx="1" fill={C.purple}/></svg>,
              val: `${formatHours(loggedMonthHours)} / ${totalMonthHours}`, unit:"h",
              pct: utilizationPct,
              hide: groupBy === "employee",
            },
          ].filter(s => !s.hide).map((s, i) => (
            <div key={i} className="sc" style={{ borderLeftColor: s.borderColor }}>

              <div className="sc-top">
                <div className="sc-ico" style={{ background: s.iconBg }}>{s.icon}</div>
                <span className="sc-lbl">{s.label}</span>
              </div>
              <div>
                <span className="sc-val" style={{ color: s.color }}>{s.val}</span>
                <span className="sc-unit">{s.unit}</span>
              </div>
              {s.pct !== undefined && (
                <>
                  <div className="sc-bar">
                    <div className="sc-fill" style={{ width:`${s.pct}%`, background: s.pct >= 80 ? C.green : s.pct >= 40 ? C.amberMd : C.red }} />
                  </div>
                  <div className="sc-meta">
                    <span className="sc-sub">{workingDays} working days · {totalMonthHours}h target</span>
                    <span className="sc-pct" style={{ color: s.pct >= 80 ? C.green : s.pct >= 40 ? C.amber : C.red }}>{s.pct}%</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>{/* end ss */}

        {/* ═══════════════════ TIMESHEET GRID ═══════════════════ */}
        <div className="gc">
          {dataLoading && (
            <div className="ov">
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                <div className="sp" />
                <span style={{ fontSize:12, color:C.n500, fontWeight:500 }}>Loading…</span>
              </div>
            </div>
          )}

          <div className="gs">
            <table className="tbl" style={{ minWidth: COL_W.iss + days.length * COL_W.day + COL_W.tot }}>

              {/* ── THEAD ── */}
              <thead style={{ position:"sticky", top:0, zIndex:25 }}>
                <tr>
                  <th className="th-i">{groupBy === "employee" ? "Employee / Issue" : "Issue"}</th>
                  {days.map((day, i) => {
                    const st = getDayStatus(day);
                    const ds = DAY_STATUS_STYLES[st];
                    const isToday = isSameDay(day, new Date());
                    const hrs = getDayTotal(day);
                    const numColor = isToday ? C.blue : st==="off"||st==="leave" ? C.n200 : st==="met" ? C.green : st==="partial" ? C.amber : C.n900;
                    const dayColor = isToday ? C.blue : st==="off"||st==="leave" ? C.n200 : C.n400;
                    return (
                      <th key={i} className="th-d" title={
                        st==="off" ? "Day off" : st==="leave" ? "On leave" :
                        hrs ? `${formatHours(hrs)}h logged` : "No hours logged"}>
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", padding:"8px 2px", gap:2 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:numColor, lineHeight:1 }}>{format(day, "d")}</span>
                          <span className="dn" style={{ color: dayColor }}>{format(day, "EEE")}</span>
                        </div>
                      </th>
                    );
                  })}
                  <th className="th-t">Total</th>
                </tr>
              </thead>

              {/* ── TBODY ── */}
              <tbody>
                {/* ══ GROUP BY EMPLOYEE mode ══ */}
                {groupBy === "employee" && (
                  allEmpLoading ? (
                    <tr><td colSpan={days.length + 2} style={{ textAlign:"center", padding:40, color:C.n500 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                        <div className="sp" />
                        <span>Loading all employees…</span>
                      </div>
                    </td></tr>
                  ) : employees.length === 0 ? (
                    <tr><td colSpan={days.length + 2} className="es">
                      <div className="es-icon">👥</div>
                      <div className="es-title">No employees found</div>
                      <div className="es-sub">No employees are assigned to this project.</div>
                    </td></tr>
                  ) : employees.map((emp) => {
                    const empTasks = allEmpData[emp.id]?.tasks || [];
                    const empLogs  = allEmpData[emp.id]?.worklogs || [];
                    const isExpanded = expandedGroups.has(String(emp.id));
                    const empPeriodTotal = round2(empLogs.filter(l => billableFilter === "all" || l.billingType === billableFilter).reduce((s,l) => s+l.hours, 0));
                    const dotColor = (task) =>
                      task.taskType === "Bug" ? "#DE350B" :
                      task.taskType === "Epic" ? "#6554C0" :
                      task.taskType === "Story" ? "#36B37E" : C.blue;

                    return (
                      <React.Fragment key={`emp-${emp.id}`}>
                       {/* Employee group header row  */}
                      <tr key={`emp-${emp.id}`} className="grp-hdr" onClick={() => toggleGroup(String(emp.id))} style={{ cursor:"pointer" }}>
                        <td className="td-i grp-hdr" style={{ background:C.n20 }}>
                          <div className="grp-toggle">
                            <span className={`grp-arrow${isExpanded ? " open" : ""}`}>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                            <div className="grp-av">
                              {emp.photoPath
                                ? <img src={`${API_BASE_URL}/uploads/${emp.photoPath}`} onError={e => { e.currentTarget.src="/default-avatar.png"; }} alt="" />
                                : emp.firstName?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <div className="grp-name">{emp.firstName} {emp.lastName}</div>
                              {emp.designation && <div className="grp-count">{empTasks.length} issue{empTasks.length !== 1 ? "s" : ""}</div>}
                            </div>
                          </div>
                        </td>
                        {days.map((day, j) => {
                          const dayTot = getEmpDayTotal(emp.id, day, billableFilter === "all" ? undefined : billableFilter);
                          const st = getDayStatus(day);
                          const isOff = st === "off" || st === "leave";
                          return (
                            <td key={j} className="td-d" style={{ background: isOff ? C.n40 : C.n20, borderRight:"1px solid "+C.n100 }}>
                              {dayTot > 0
                                ? <span className="grp-day" style={{ color:C.n700, fontWeight:700 }}>{formatHours(dayTot)}</span>
                                : isOff ? <span className="em">–</span> : null}
                            </td>
                          );
                        })}
                        <td className="td-t" style={{ background:C.n20 }}>
                          <span className="grp-total">{empPeriodTotal > 0 ? formatHours(empPeriodTotal) : <span style={{color:C.n200}}>—</span>}</span>
                        </td>
                      </tr>

                      {/* ── Employee's task sub-rows ── */}
                      {isExpanded && empTasks.map((task, ti) => {
                        const taskTotal = round2(days.reduce((s,day) => s + getHoursForEmp(emp.id, task.id, day), 0));
                        return (
                          <tr key={`emp-${emp.id}-task-${task.id}`}>
                            <td className="td-i sub-emp">
                              <a className="ia" href={`/tasks?taskId=${task.id}`}
                                onClick={e => { e.preventDefault(); localStorage.setItem("activeTaskId", String(task.id)); localStorage.setItem("activeTaskObj", JSON.stringify(task)); window.open(`/tasks?taskId=${task.id}`,"_blank"); }}>
                                <div className="ii" style={{ paddingLeft:36 }}>
                                  <div className="idot" style={{ background: dotColor(task) }} />
                                  <div className="ib">
                                    <div className="ic">{task.taskCode}</div>
                                    <div className="it" title={task.title}>{task.title || `Task ${task.id}`}</div>
                                    
                                  </div>
                                </div>
                              </a>
                            </td>
                            {days.map((day, j) => {
                              const logs = empLogs.filter(l => l.taskId === task.id && isSameDay(new Date(l.date), day) && (billableFilter === "all" || l.billingType === billableFilter));
                              const h = round2(logs.reduce((s,l) => s+l.hours, 0));
                              const st = getDayStatus(day);
                              const isOff = st === "off" || st === "leave";
                              const hasBill = logs.some(l => l.billingType === "billable");
                              const hasNon  = logs.some(l => l.billingType === "non-billable");
                              const pillBg    = !h ? "transparent" : (hasBill&&hasNon) ? C.purpleLt : hasBill ? C.blueLt : C.amberLt;
                              const pillColor = !h ? "transparent" : (hasBill&&hasNon) ? C.purple    : hasBill ? C.blue   : C.amber;
                              return (
                                <td key={j} className="td-d" style={{ background: isOff?(st==="leave"?"#FFF8F7":C.n20):"", borderRight:"1px solid "+C.n100 }}
                                  title={logs.map(l=>`${l.description||"No description"} (${l.hours}h)`).join("\n")||undefined}>
                                  {h > 0
                                    ? <span className="hp" style={{ background:pillBg, color:pillColor }}>{formatHours(h)}</span>
                                    : isOff ? <span className="em">–</span> : null}
                                </td>
                              );
                            })}
                            <td className="td-t">
                              {taskTotal > 0
                                ? <span style={{ color:C.blue }}>{formatHours(taskTotal)}</span>
                                : <span style={{ color:C.n200 }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                    );
                  })
                )}

                {/* ══ GROUP BY ISSUE mode ══ */}
                {groupBy === "issue" && (
                  tasks.length > 0 ? tasks.map((task, i) => {
                    const rowTotal = round2(days.reduce((sum, day) => sum + getHours(task.id, day), 0));
                    const dotColor =
                      task.taskType === "Bug"   ? "#DE350B" :
                      task.taskType === "Epic"  ? "#6554C0" :
                      task.taskType === "Story" ? "#36B37E" : C.blue;

                    return (
                      <tr key={task.id || i}>
                        <td className="td-i">
                          <a className="ia"
                            href={`/tasks?taskId=${task.id}`}
                            onClick={e => {
                              e.preventDefault();
                              localStorage.setItem("activeTaskId", String(task.id));
                              localStorage.setItem("activeTaskObj", JSON.stringify(task));
                              window.open(`/tasks?taskId=${task.id}`, "_blank");
                            }}>
                            <div className="ii">
                              <div className="idot" style={{ background: dotColor }} />
                              <div className="ib">
                                <div className="ic">{task.taskCode}</div>
                                <div className="it" title={task.title}>{task.title || `Task ${task.id}`}</div>
                                
                              </div>
                            </div>
                          </a>
                        </td>

                        {days.map((day, j) => {
                          const logs = filteredWorklogs.filter(l =>
                            l.taskId === task.id && isSameDay(new Date(l.date), day)
                          );
                          const h = round2(logs.reduce((s, l) => s + (l.hours || 0), 0));
                          const st = getDayStatus(day);
                          const isOff = st === "off" || st === "leave";
                          const hasBill = logs.some(l => l.billingType === "billable");
                          const hasNon  = logs.some(l => l.billingType === "non-billable");
                          const pillBg    = !h ? "transparent" : (hasBill && hasNon) ? C.purpleLt : hasBill ? C.blueLt : C.amberLt;
                          const pillColor = !h ? "transparent" : (hasBill && hasNon) ? C.purple    : hasBill ? C.blue   : C.amber;
                          const cellBg = isOff ? (st === "leave" ? "#FFF8F7" : C.n20) : "";

                          return (
                            <td key={j} className="td-d" style={{ background: cellBg }}
                              title={logs.map(l => `${l.description || "No description"} (${l.hours}h)`).join("\n") || undefined}>
                              {h > 0 ? (
                                <span className="hp" style={{ background: pillBg, color: pillColor }}>{formatHours(h)}</span>
                              ) : isOff ? (
                                <span className="em">–</span>
                              ) : null}
                            </td>
                          );
                        })}

                        <td className="td-t">
                          {rowTotal > 0
                            ? <span style={{ color: C.blue }}>{formatHours(rowTotal)}</span>
                            : <span style={{ color: C.n200 }}>—</span>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={days.length + 2} className="es">
                        <div className="es-icon">📋</div>
                        <div className="es-title">No time logged</div>
                        <div className="es-sub">No worklogs found for this employee in the selected period.</div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>

              {/* ── TFOOT ── */}
              <tfoot className="tf">
                {[
                  { label:"Billable",     type:"billable",     color:C.blue,  grand:totalBillable     },
                  { label:"Non-Billable", type:"non-billable", color:C.red,   grand:totalNonBillable  },
                  { label:"Daily Total",  type:null,           color:C.n900,  grand:totalHours        },
                ].map(({ label, type, color, grand }) => (
                  <tr key={label}>
                    <td className="tfl" style={{ color }}>{label}</td>
                    {days.map((day, i) => {
                      const tot = groupBy === "employee"
                        ? round2(employees.reduce((s, emp) => s + getEmpDayTotal(emp.id, day, type || undefined), 0))
                        : round2(tasks.reduce((s, t) => s + getHours(t.id, day, type || undefined), 0));
                      const st = getDayStatus(day);
                      const isOff = st === "off" || st === "leave";
                      return (
                        <td key={i} className="tfd" style={{ background: isOff ? C.n40 : C.n20, color, borderRight:"1px solid "+C.n100, borderBottom:"1px solid "+C.n100 }}>
                          {tot ? formatHours(tot) : ""}
                        </td>
                      );
                    })}
                    <td className="tft" style={{ color }}>
                      {groupBy === "employee"
                        ? formatHours(round2(employees.reduce((s, emp) => s + getEmpPeriodTotal(emp.id, type || undefined), 0)))
                        : formatHours(grand)}
                    </td>
                  </tr>
                ))}
              </tfoot>

            </table>
          </div>
        </div>

      </div>
    </>
  );
};

export default TimesheetView;